import React, { useState, useCallback, useRef, useMemo } from 'react';
import html2pdf from 'html2pdf.js';

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

// ========== TEMPLATES ==========
const FLYER_TEMPLATES = [
  { id: 'blank', name: 'Leer', desc: 'Freies Layout', bg: '#020617', layout: [] },
  { id: 'promo1', name: 'Angebot', desc: '1 Bild + Text', bg: 'linear-gradient(135deg, #0c1445, #1e3a5f)', layout: [
    { type: 'text', x: 50, y: 40, text: 'MEGA ANGEBOT', fontSize: 42, fontWeight: 800, color: '#38bdf8', textAlign: 'center' },
    { type: 'text', x: 50, y: 55, text: 'Jetzt zuschlagen!', fontSize: 22, fontWeight: 600, color: '#fff', textAlign: 'center' },
    { type: 'image', x: 50, y: 78, width: 60, height: 30, placeholder: '📷 Bild hier' },
  ]},
  { id: 'duo', name: '2er Split', desc: '2 Bilder nebeneinander', bg: 'linear-gradient(180deg, #020617, #0f172a)', layout: [
    { type: 'text', x: 50, y: 10, text: 'HEADLINE', fontSize: 36, fontWeight: 800, color: '#fff', textAlign: 'center' },
    { type: 'image', x: 25, y: 50, width: 42, height: 50, placeholder: '📷 Bild 1' },
    { type: 'image', x: 75, y: 50, width: 42, height: 50, placeholder: '📷 Bild 2' },
    { type: 'text', x: 50, y: 90, text: 'Beschreibung hier', fontSize: 16, fontWeight: 500, color: 'rgba(255,255,255,0.6)', textAlign: 'center' },
  ]},
  { id: 'quad', name: '4er Grid', desc: '4 Bilder im Raster', bg: '#0f172a', layout: [
    { type: 'text', x: 50, y: 8, text: 'UNSERE TOP-ANGEBOTE', fontSize: 28, fontWeight: 800, color: '#00f5d4', textAlign: 'center' },
    { type: 'image', x: 25, y: 35, width: 42, height: 30, placeholder: '📷 1' },
    { type: 'image', x: 75, y: 35, width: 42, height: 30, placeholder: '📷 2' },
    { type: 'image', x: 25, y: 70, width: 42, height: 30, placeholder: '📷 3' },
    { type: 'image', x: 75, y: 70, width: 42, height: 30, placeholder: '📷 4' },
    { type: 'text', x: 50, y: 95, text: 'Gültig bis XX.XX.XXXX', fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.72)', textAlign: 'center' },
  ]},
  { id: 'event', name: 'Event', desc: 'Veranstaltung / Aktion', bg: 'linear-gradient(135deg, #1a0533, #0c1445)', layout: [
    { type: 'text', x: 50, y: 15, text: '🎉 EVENT', fontSize: 48, fontWeight: 800, color: '#ff66a2', textAlign: 'center' },
    { type: 'text', x: 50, y: 30, text: 'Datum & Ort hier eintragen', fontSize: 18, fontWeight: 600, color: 'rgba(255,255,255,0.75)', textAlign: 'center' },
    { type: 'image', x: 50, y: 60, width: 70, height: 40, placeholder: '📷 Event-Bild' },
    { type: 'text', x: 50, y: 90, text: 'Weitere Infos unter...', fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.65)', textAlign: 'center' },
  ]},
];

// ========== GLASS CARD ==========
function GlassCard({ children, style = {} }) {
  return (
    <div style={{ borderRadius: 16, border: '1px solid rgba(56,189,248,0.12)', background: 'rgba(56,189,248,0.03)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', padding: '16px 14px', ...style }}>
      {children}
    </div>
  );
}

// ========== ELEMENT COMPONENT ==========
function FlyerElement({ el, isSelected, onClick, onUpdate, onImageUpload, scale }) {
  const style = {
    position: 'absolute',
    left: `${el.x}%`, top: `${el.y}%`,
    transform: 'translate(-50%, -50%)',
    cursor: 'pointer',
    outline: isSelected ? '2px solid #38bdf8' : 'none',
    outlineOffset: 4,
    borderRadius: 6,
    transition: 'outline 0.15s',
    zIndex: isSelected ? 10 : 1,
  };

  if (el.type === 'text') {
    return (
      <div onClick={(e) => { e.stopPropagation(); onClick(); }} style={{ ...style, width: `${el.width || 90}%`, padding: '4px 8px', textAlign: el.textAlign || 'center' }}>
        {isSelected ? (
          <textarea
            value={el.text}
            onChange={(e) => onUpdate({ text: e.target.value })}
            style={{ width: '100%', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(56,189,248,0.3)', borderRadius: 4, color: el.color || '#fff', fontSize: (el.fontSize || 16) * scale, fontWeight: el.fontWeight || 500, fontFamily: "'Poppins', system-ui, sans-serif", textAlign: el.textAlign || 'center', resize: 'none', outline: 'none', padding: '4px' }}
            rows={Math.max(1, Math.ceil((el.text || '').length / 30))}
            autoFocus
          />
        ) : (
          <div style={{ color: el.color || '#fff', fontSize: (el.fontSize || 16) * scale, fontWeight: el.fontWeight || 500, fontFamily: "'Poppins', system-ui, sans-serif", textAlign: el.textAlign || 'center', lineHeight: 1.3 }}>
            {el.text || 'Text eingeben...'}
          </div>
        )}
      </div>
    );
  }

  if (el.type === 'image') {
    const imgStyle = { ...style, width: `${el.width || 40}%`, height: `${el.height || 30}%` };
    if (el.src) {
      return (
        <div onClick={(e) => { e.stopPropagation(); onClick(); }} style={{ ...imgStyle, overflow: 'hidden', borderRadius: 12 }}>
          <img src={el.src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }} />
        </div>
      );
    }
    return (
      <div onClick={(e) => { e.stopPropagation(); onClick(); }} style={{ ...imgStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '2px dashed rgba(56,189,248,0.25)', borderRadius: 12, background: 'rgba(56,189,248,0.04)', gap: 6 }}>
        <span style={{ fontSize: 24 }}>{el.placeholder || '📷'}</span>
        <label style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.72)', cursor: 'pointer', padding: '4px 10px', borderRadius: 6, background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.2)' }}>
          Bild hochladen
          <input type="file" accept="image/*" onChange={(e) => onImageUpload(e.target.files[0])} style={{ display: 'none' }} />
        </label>
      </div>
    );
  }

  return null;
}

// ========== MAIN COMPONENT ==========
export function FlyerGenerator() {
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [flyerBg, setFlyerBg] = useState('#020617');
  const [flyerTitle, setFlyerTitle] = useState('Mein Flyer');
  const [uploadedImages, setUploadedImages] = useState([]); // max 4
  const canvasRef = useRef(null);

  const selectedEl = useMemo(() => elements.find(e => e.id === selectedId), [elements, selectedId]);

  const applyTemplate = useCallback((tpl) => {
    setActiveTemplate(tpl.id);
    setFlyerBg(tpl.bg);
    setElements(tpl.layout.map(el => ({ ...el, id: generateId() })));
    setSelectedId(null);
  }, []);

  const updateElement = useCallback((id, updates) => {
    setElements(prev => prev.map(el => el.id === id ? { ...el, ...updates } : el));
  }, []);

  const deleteElement = useCallback((id) => {
    setElements(prev => prev.filter(el => el.id !== id));
    if (selectedId === id) setSelectedId(null);
  }, [selectedId]);

  const addText = useCallback(() => {
    const el = { id: generateId(), type: 'text', x: 50, y: 50, text: 'Neuer Text', fontSize: 20, fontWeight: 600, color: '#fff', textAlign: 'center' };
    setElements(prev => [...prev, el]);
    setSelectedId(el.id);
  }, []);

  const addImage = useCallback(() => {
    if (uploadedImages.length >= 4 && !elements.some(e => e.type === 'image' && !e.src)) {
      return; // max 4
    }
    const el = { id: generateId(), type: 'image', x: 50, y: 50, width: 50, height: 35, placeholder: '📷 Bild' };
    setElements(prev => [...prev, el]);
    setSelectedId(el.id);
  }, [uploadedImages.length, elements]);

  const handleImageUpload = useCallback((id, file) => {
    if (!file || uploadedImages.length >= 4) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const src = e.target.result;
      setUploadedImages(prev => [...prev, { id, src }].slice(0, 4));
      updateElement(id, { src });
    };
    reader.readAsDataURL(file);
  }, [uploadedImages.length, updateElement]);

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const handleDownloadPDF = async () => {
    setIsGeneratingPdf(true);
    setSelectedId(null); // Deselect everything to hide outlines
    
    // Kurze Pause fürs UI Update (Rahmen weg)
    await new Promise(r => setTimeout(r, 100));

    const element = canvasRef.current;
    if (!element) {
      setIsGeneratingPdf(false);
      return;
    }

    const opt = {
      margin:       0, // Randlos für Flyer
      filename:     `TEF-Flyer-${Date.now()}.pdf`,
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2, useCORS: true, logging: false },
      jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    try {
      await html2pdf().set(opt).from(element).save();
    } catch (err) {
      console.error('[PDF Export Flyer] Error:', err);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Scale text for responsive canvas
  const scale = typeof window !== 'undefined' ? Math.min(1, window.innerWidth / 800) : 1;

  // Template selection screen
  if (!activeTemplate) {
    return (
      <div style={{ minHeight: '100vh', background: '#020617', color: '#fff', fontFamily: "'Poppins', system-ui, sans-serif" }}>
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '20px 16px' }}>
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, background: 'linear-gradient(135deg, #ff66a2, #38bdf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>🎨 Flyer Generator</div>
            <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.72)', marginTop: 4 }}>Erstelle druckfertige Flyer — wähle ein Template oder starte leer</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
            {FLYER_TEMPLATES.map(tpl => (
              <button key={tpl.id} onClick={() => applyTemplate(tpl)} style={{ padding: '20px 14px', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)', background: typeof tpl.bg === 'string' && tpl.bg.includes('gradient') ? tpl.bg : tpl.bg, cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s', minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#fff' }}>{tpl.name}</div>
                <div style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.72)', marginTop: 2 }}>{tpl.desc}</div>
                <div style={{ fontSize: '0.5rem', color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>{tpl.layout.filter(e => e.type === 'image').length} Bild(er) · {tpl.layout.filter(e => e.type === 'text').length} Text(e)</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Editor
  return (
    <div style={{ minHeight: '100vh', background: '#020617', color: '#fff', fontFamily: "'Poppins', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '12px 16px 80px' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12, alignItems: 'center' }}>
          <button onClick={() => { setActiveTemplate(null); setElements([]); setUploadedImages([]); setSelectedId(null); }} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '6px 12px', color: 'rgba(255,255,255,0.82)', cursor: 'pointer', fontSize: '0.65rem', fontWeight: 700 }}>← Templates</button>
          <button onClick={addText} style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.2)', borderRadius: 10, padding: '6px 12px', color: '#38bdf8', cursor: 'pointer', fontSize: '0.65rem', fontWeight: 700 }}>＋ Text</button>
          <button onClick={addImage} disabled={uploadedImages.length >= 4} style={{ background: uploadedImages.length >= 4 ? 'rgba(255,255,255,0.03)' : 'rgba(0,245,212,0.08)', border: `1px solid ${uploadedImages.length >= 4 ? 'rgba(255,255,255,0.05)' : 'rgba(0,245,212,0.2)'}`, borderRadius: 10, padding: '6px 12px', color: uploadedImages.length >= 4 ? 'rgba(255,255,255,0.5)' : '#00f5d4', cursor: uploadedImages.length >= 4 ? 'default' : 'pointer', fontSize: '0.65rem', fontWeight: 700 }}>＋ Bild ({uploadedImages.length}/4)</button>
          {selectedId && (
            <button onClick={() => deleteElement(selectedId)} style={{ background: 'rgba(255,0,110,0.08)', border: '1px solid rgba(255,0,110,0.2)', borderRadius: 10, padding: '6px 12px', color: '#ff006e', cursor: 'pointer', fontSize: '0.65rem', fontWeight: 700 }}>🗑️ Löschen</button>
          )}
          <div style={{ flex: 1 }} />
          <button onClick={handleDownloadPDF} disabled={isGeneratingPdf} style={{ background: isGeneratingPdf ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #38bdf8, #0284c7)', border: 'none', borderRadius: 10, padding: '8px 18px', color: '#fff', cursor: isGeneratingPdf ? 'wait' : 'pointer', fontSize: '0.72rem', fontWeight: 800, boxShadow: isGeneratingPdf ? 'none' : '0 4px 16px rgba(56,189,248,0.3)' }}>{isGeneratingPdf ? 'Generiere...' : '🖨️ Drucken / PDF'}</button>
        </div>

        {/* Properties Panel (when element selected) */}
        {selectedEl && (
          <GlassCard style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', fontWeight: 700 }}>{selectedEl.type === 'text' ? '📝 Text' : '🖼️ Bild'}</span>
              {selectedEl.type === 'text' && (
                <>
                  <input type="number" value={selectedEl.fontSize || 16} onChange={e => updateElement(selectedId, { fontSize: parseInt(e.target.value) || 16 })} style={{ width: 50, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#fff', fontSize: '0.7rem', padding: '4px 6px', textAlign: 'center', outline: 'none' }} title="Schriftgröße" />
                  <span style={{ fontSize: '0.5rem', color: 'rgba(255,255,255,0.6)' }}>px</span>
                  <input type="color" value={selectedEl.color || '#ffffff'} onChange={e => updateElement(selectedId, { color: e.target.value })} style={{ width: 28, height: 28, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, cursor: 'pointer', padding: 0 }} title="Farbe" />
                  <select value={selectedEl.fontWeight || 500} onChange={e => updateElement(selectedId, { fontWeight: parseInt(e.target.value) })} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#fff', fontSize: '0.6rem', padding: '4px 6px', outline: 'none' }}>
                    <option value={400}>Normal</option>
                    <option value={600}>Semi-Bold</option>
                    <option value={700}>Bold</option>
                    <option value={800}>Extra-Bold</option>
                  </select>
                </>
              )}
              {/* Position */}
              <span style={{ fontSize: '0.5rem', color: 'rgba(255,255,255,0.6)', marginLeft: 8 }}>X</span>
              <input type="range" min="5" max="95" value={selectedEl.x} onChange={e => updateElement(selectedId, { x: parseInt(e.target.value) })} style={{ width: 60, accentColor: '#38bdf8' }} />
              <span style={{ fontSize: '0.5rem', color: 'rgba(255,255,255,0.6)' }}>Y</span>
              <input type="range" min="5" max="95" value={selectedEl.y} onChange={e => updateElement(selectedId, { y: parseInt(e.target.value) })} style={{ width: 60, accentColor: '#38bdf8' }} />
              {selectedEl.type === 'image' && (
                <>
                  <span style={{ fontSize: '0.5rem', color: 'rgba(255,255,255,0.6)', marginLeft: 8 }}>Breite</span>
                  <input type="range" min="10" max="95" value={selectedEl.width || 40} onChange={e => updateElement(selectedId, { width: parseInt(e.target.value) })} style={{ width: 60, accentColor: '#00f5d4' }} />
                </>
              )}
            </div>
          </GlassCard>
        )}

        {/* Canvas */}
        <div
          id="flyer-canvas"
          ref={canvasRef}
          onClick={() => setSelectedId(null)}
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '210 / 297', // A4
            background: flyerBg,
            borderRadius: 16,
            border: '1px solid rgba(255,255,255,0.08)',
            overflow: 'hidden',
            boxShadow: '0 12px 48px rgba(0,0,0,0.4)',
          }}
        >
          {elements.map(el => (
            <FlyerElement
              key={el.id}
              el={el}
              isSelected={selectedId === el.id}
              onClick={() => setSelectedId(el.id)}
              onUpdate={(updates) => updateElement(el.id, updates)}
              onImageUpload={(file) => handleImageUpload(el.id, file)}
              scale={scale}
            />
          ))}

          {elements.length === 0 && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <span style={{ fontSize: 40 }}>🎨</span>
              <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.65)' }}>Leerer Flyer — füge Texte und Bilder hinzu</span>
            </div>
          )}
        </div>

        {/* Info */}
        <div style={{ marginTop: 12, fontSize: '0.55rem', color: 'rgba(255,255,255,0.6)', textAlign: 'center' }}>
          A4 Format (210 × 297 mm) · Klicke auf Elemente zum Bearbeiten · „Drucken / PDF" zum Exportieren · Max. 4 Bilder
        </div>
      </div>
    </div>
  );
}

export default FlyerGenerator;
